import * as React from 'react';
import { useSelector } from 'react-redux';

import { OFFER_TEMPLATE, OFFERS_LIMIT } from './constants';
import { OfferItem, Offers, Features } from '../../components/Offers';
import { Section } from '../../components/Section';
import { selectOffers, selectOffersStatus } from '../../selectors/offers';
import { ERequestStatus } from '../../services/requestStatus';
import { TOffers, IOffer } from '../../types/offers';

export const OffersContainer: React.FC = () => {
  const status = useSelector(selectOffersStatus);
  const offersRaw = useSelector(selectOffers);
  const isPending = [ERequestStatus.Initial, ERequestStatus.Loading].includes(status);
  const [isPaginationPending, setPaginationPending] = React.useState<boolean>(false);

  const [offersLimit, setOffersLimit] = React.useState<number>(OFFERS_LIMIT);
  const withShowMore = status === ERequestStatus.Initial || offersRaw.length > offersLimit;
  const offers: TOffers = React.useMemo(() => {
    // Array<IOffer>(offersLimit).fill(OFFER_TEMPLATE)
    return isPending ? Array<IOffer>(OFFERS_LIMIT).fill(OFFER_TEMPLATE) : offersRaw.slice(0, offersLimit);
  }, [isPending, offersRaw, offersLimit]);

  React.useEffect(() => {
    setOffersLimit(OFFERS_LIMIT);
  }, [offersRaw]);

  const handleShowMore = React.useCallback(() => {
    if (isPending || isPaginationPending) {
      return;
    }

    setTimeout(() => {
      const nextLimit = Math.min(offersLimit + OFFERS_LIMIT, offersRaw.length);

      setOffersLimit(nextLimit);
      setPaginationPending(false);
    }, 1000);

    setPaginationPending(true);
  }, [isPaginationPending, isPending, offersLimit, offersRaw.length]);

  const handleOfferClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      if (isPending) {
        return;
      }

      window.location.href = event.currentTarget.href;
    },
    [isPending],
  );

  return (
    <Section title="Могут подойти" id="offers">
      <Offers
        pending={isPending}
        paginationPending={isPaginationPending || isPending}
        withShowMore={withShowMore}
        onShowMore={handleShowMore}
      >
        {offers.map((offer, index) => (
          <OfferItem
            key={`offer_${index}`}
            url={offer.url}
            photoUrl={offer.photoUrl}
            price={offer.price}
            features={<Features features={offer.features} />}
            underground={offer.underground}
            pending={isPending}
            onClick={handleOfferClick}
          />
        ))}
      </Offers>
    </Section>
  );
};

OffersContainer.displayName = 'OffersContainer';
